import React, { useState, useRef, useEffect } from 'react';
import { Download, Printer, Save, MoveUp, MoveDown } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import invoiceService from '../../services/invoiceService';
import { toast } from '@/components/UI/use-toast';

// Utility Functions
const formatDate = (date) => {
  if (!date) return '';
  const options = {
    timeZone: 'Asia/Dubai',
    month: 'long',
    year: 'numeric',
  };
  const d = new Date(date);
  const dubaiDate = d.toLocaleString('en-US', { timeZone: 'Asia/Dubai' });
  const dubaiD = new Date(dubaiDate);
  
  return `${dubaiD.getDate()} ${new Intl.DateTimeFormat('en-US', options).format(d).toUpperCase()}`;
};

const formatNumber = (num) => {
  if (num === undefined || num === null) return '0.00';
  if (num === 'Na' || num === 'NA' || num === 'NaN') return 'NA';
  
  const value = parseFloat(num);
  if (isNaN(value)) return 'NA';
  if (value === 0) return '0.00';
  
  const isNegative = value < 0;
  const formattedNum = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return isNegative ? `(${formattedNum})` : formattedNum;
};

const numberToWords = (number) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const scales = ['', 'Thousand', 'Million', 'Billion'];

  const convertGroup = (n, isLastGroup = false) => {
    let str = '';
    if (n >= 100) {
      str += ones[Math.floor(n / 100)] + ' Hundred';
      n %= 100;
      if (n > 0 && isLastGroup) str += ' and';
    }
    if (n >= 20) {
      str += (str && !str.endsWith('and') ? ' ' : ' ') + tens[Math.floor(n / 10)];
      if (n % 10 > 0) str += '-' + ones[n % 10];
    } else if (n >= 10) {
      str += (str && !str.endsWith('and') ? ' ' : ' ') + teens[n - 10];
    } else if (n > 0) {
      str += (str && !str.endsWith('and') ? ' ' : ' ') + ones[n];
    }
    return str.trim();
  };

  if (!number || isNaN(number)) return 'Zero Only.';
  if (number === 0) return 'Zero Only.';

  const num = Math.abs(number);
  const decimalPart = Math.round((num % 1) * 100);
  let str = '';
  let scale = 0;
  let remaining = Math.floor(num);
  
  while (remaining > 0) {
    const n = remaining % 1000;
    if (n !== 0) {
      const group = convertGroup(n, remaining < 1000);
      str = group + (scale > 0 ? ' ' + scales[scale] + ' ' : ' ') + str;
    }
    scale++;
    remaining = Math.floor(remaining / 1000);
  }

  str = str.trim();
  const decimalStr = decimalPart > 0 ? ` & ${decimalPart.toString().padStart(2, '0')}/100` : '';
  return str + (decimalStr || '') + ' Only.';
};

const InvoicePreview = ({ formData = {}, client = {}, project = {}, onBack }) => {
  const [isPreview, setIsPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [manualAmounts, setManualAmounts] = useState({});
  const [forceUpdate, setForceUpdate] = useState(false);
  const hasOtherDeduction = formData.deductions?.otherDeduction?.checked;
  const invoiceRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Add state for custom row order
  const [customRowOrder, setCustomRowOrder] = useState([]);
  
  // Add print-specific styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @page {
        size: A4 portrait;
        margin: 0;
      }
      @media print {
        body * {
          visibility: hidden;
        }
        #invoice-content,
        #invoice-content * {
          visibility: visible;
        }
        #invoice-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 210mm;
          height: 297mm;
          padding: 25mm 10mm 25mm 8mm;
        }
        
        /* Specific table styles for printing */
        #invoice-content table {
          border-collapse: collapse !important;
          width: 100% !important;
        }
        
        #invoice-content table,
        #invoice-content th,
        #invoice-content td {
          border: 1px solid black !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        #invoice-content th,
        #invoice-content td {
          padding: 2px 4px !important;
          position: relative !important;
          background-clip: padding-box !important;
        }
        
        /* Force table rows to avoid page breaks */
        #invoice-content tr {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        /* Ensure borders print in the correct color */
        #invoice-content table,
        #invoice-content th,
        #invoice-content td {
          border-color: black !important;
          border-width: 1px !important;
          border-style: solid !important;
        }
        
        /* Preserve text colors in print */
        .text-red-600 { color: rgb(220, 38, 38) !important; }
        .text-blue-500, .text-blue-600 { color: rgb(59, 130, 246) !important; }
        .text-green-600 { color: rgb(16, 185, 129) !important; }
        .bg-yellow-300 { background-color: rgb(253, 224, 71) !important; }
        
        /* Hide row control buttons when printing */
        .row-controls, .print-hidden {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Decimal Editor Component
  const DecimalEditor = ({ value, identifier, field, isTotal = false }) => {
    if (value === 'Na' || value === 'NA' || value === 'NaN' || value === null) {
      return <div className="text-center">NA</div>;
    }
    
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return value;
    
    const actualValue = getAdjustedAmount(numericValue, identifier, field);
    if (actualValue === 0) return '0.00';
    
    const absValue = Math.abs(actualValue);
    const wholeStr = Math.floor(absValue).toString();
    const lastFourDigits = wholeStr.slice(-4).padStart(4, '0');
    const decimals = absValue.toFixed(2).split('.')[1] || '00';
    
    return (
      <div className="relative group">
        <span>{formatNumber(actualValue)}</span>
        {!isPreview && (
          <div className="hidden group-hover:block absolute right-0 top-full mt-1 bg-white shadow-lg rounded p-2 z-10">
            <div className="w-48 border rounded-lg divide-y">
              <div className="p-2 space-y-1">
                <div className="text-xs text-gray-500 font-medium">Last 4 digits:</div>
                <input
                  type="number"
                  min="0"
                  max="9999"
                  value={lastFourDigits}
                  onChange={(e) => handleDecimalEdit(identifier, field, e.target.value, false, isTotal)}
                  className="w-full text-sm border rounded px-2 py-1"
                />
              </div>
              <div className="p-2 space-y-1">
                <div className="text-xs text-gray-500 font-medium">Decimals:</div>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={decimals}
                  onChange={(e) => handleDecimalEdit(identifier, field, e.target.value, true, isTotal)}
                  className="w-full text-sm border rounded px-2 py-1"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Handle decimal editing
  const handleDecimalEdit = (identifier, field, value, isDecimal = false, isTotal = false) => {
    if (value === '' || isNaN(value)) return;
    
    let newValue;
    let currentValue;
    
    if (isTotal) {
      const totals = calculateTotals(getOrderedRows());
      currentValue = totals[field];
    } else {
      const row = getOrderedRows()[identifier];
      if (!row) return;
      currentValue = parseFloat(row[field]);
    }

    if (isNaN(currentValue)) return;

    const manualKey = `${identifier}-${field}`;
    let existingValue = manualAmounts[manualKey] || currentValue;
    
    if (isDecimal) {
      const wholeNumber = Math.floor(Math.abs(existingValue));
      newValue = wholeNumber + (parseFloat(value) / 100);
    } else {
      const prefix = Math.floor(Math.abs(existingValue)).toString().slice(0, -4) || '0';
      let newSuffix = parseInt(value);
      newSuffix = Math.max(0, Math.min(9999, newSuffix));
      newValue = parseFloat(prefix + newSuffix.toString().padStart(4, '0'));
    }

    const isDeduction = !isTotal && getOrderedRows()[identifier]?.isDeduction;
    if (isDeduction) newValue = -Math.abs(newValue);
    
    setManualAmounts(prev => ({
      ...prev,
      [manualKey]: newValue
    }));
    
    // Force update to refresh amount in words
    setForceUpdate(prev => !prev);
  };

  const getAdjustedAmount = (baseAmount, identifier, field) => {
    const manualKey = `${identifier}-${field}`;
    return manualAmounts[manualKey] ?? baseAmount;
  };

  // Calculate rows data
  const calculateRows = (formData) => {
    try {
      const rows = [];
      const cumulativeValue = parseFloat(formData.details?.cumulativeValue) || 0;
      const prevPayment = parseFloat(formData.details?.previousPayment) || 0;
      let currentSno = 1;
  
      // Add cumulative value row
      rows.push({
        sno: currentSno++,
        description: `Cumulative Value of Works Done + Variation works done for Period Ending <strong>${formatDate(formData.details?.certificateDate)}</strong>, Based on Payment Certificate No.<strong>${formData.details?.paymentCertificateNo}</strong>`,
        qty: 1,
        unitPrice: cumulativeValue,
        taxable: cumulativeValue,
        vatPercentage: 5,
        vatAmount: Number((cumulativeValue * 0.05).toFixed(2)),
        totalAmount: Number((cumulativeValue * 1.05).toFixed(2)),
        isDeduction: false,
        type: 'cumulative'
      });
  
      // Advance Release with updated logic to use fixed amount
      if (formData.deductions?.advanceRelease?.checked) {
        const percentage = formData.deductions.advanceRelease.percentage;
        const hasFixedAmount = formData.deductions.advanceRelease.useFixedAmount;
        const useStoredAmount = formData.deductions.advanceRelease.useStoredAmount;
        
        let taxableAmount;
        if (hasFixedAmount && useStoredAmount && formData.deductions.advanceRelease.fixedAmount > 0) {
          // Use the fixed amount from project data only if useStoredAmount is true
          taxableAmount = Number(parseFloat(formData.deductions.advanceRelease.fixedAmount).toFixed(2));
        } else {
          // Calculate based on percentage
          taxableAmount = Number((cumulativeValue * (percentage / 100)).toFixed(2));
        }
        
        const vatAmount = Number((taxableAmount * 0.05).toFixed(2));
        const totalAmount = Number((taxableAmount * 1.05).toFixed(2));
      
        rows.push({
          sno: currentSno++,
          description: `Less: ${percentage}% Advance of Total Amount`,
          qty: 1,
          unitPrice: -taxableAmount,
          taxable: -taxableAmount,
          vatPercentage: 5,
          vatAmount: -vatAmount,
          totalAmount: -totalAmount,
          isDeduction: true,
          type: 'advance_release',
          deductionData: {
            percentage,
            taxableAmount,
            vatAmount,
            totalAmount,
            useFixedAmount: hasFixedAmount && useStoredAmount
          }
        });
      }
      // Retention Release
      if (formData.deductions?.retentionRelease?.checked) {
        const percentage = formData.deductions.retentionRelease.percentage;
        const taxableAmount = Number((cumulativeValue * (percentage / 100)).toFixed(2));
        const vatAmount = Number((taxableAmount * 0.05).toFixed(2));
        const totalAmount = Number((taxableAmount * 1.05).toFixed(2));
  
        rows.push({
          sno: currentSno++,
          description: `Less: ${percentage}% Retention Release of Total Amount`,
          qty: 1,
          unitPrice: -taxableAmount,
          taxable: -taxableAmount,
          vatPercentage: 5,
          vatAmount: -vatAmount,
          totalAmount: -totalAmount,
          isDeduction: true,
          type: 'retention_release',
          deductionData: {
            percentage,
            taxableAmount,
            vatAmount,
            totalAmount
          }
        });
      }
  
      // Cumulative Retention
      if (formData.deductions?.cumulativeretention?.checked) {
        const percentage = formData.deductions.cumulativeretention.percentage;
        const taxableAmount = Number((cumulativeValue * (percentage / 100)).toFixed(2));
        const vatAmount = Number((taxableAmount * 0.05).toFixed(2));
        const totalAmount = Number((taxableAmount * 1.05).toFixed(2));
  
        rows.push({
          sno: currentSno++,
          description: `Less: ${percentage}% Cumulative Retention of Total Amount`,
          qty: 1,
          unitPrice: -taxableAmount,
          taxable: -taxableAmount,
          vatPercentage: 5,
          vatAmount: -vatAmount,
          totalAmount: -totalAmount,
          isDeduction: true,
          type: 'cumulative_retention',
          deductionData: {
            percentage,
            taxableAmount,
            vatAmount,
            totalAmount
          }
        });
      }
  
      // NCR Deduction
      if (formData.deductions?.ncrDeduction?.checked) {
        const taxableAmount = Number(parseFloat(formData.deductions.ncrDeduction.amount).toFixed(2)) || 0;
        const vatAmount = Number((taxableAmount * 0.05).toFixed(2));
        const totalAmount = Number((taxableAmount * 1.05).toFixed(2));
  
        rows.push({
          sno: currentSno++,
          description: 'Less: NCR Deduction',
          qty: 1,
          unitPrice: -taxableAmount,
          taxable: -taxableAmount,
          vatPercentage: 5,
          vatAmount: -vatAmount,
          totalAmount: -totalAmount,
          isDeduction: true,
          type: 'ncr_deduction',
          deductionData: {
            taxableAmount,
            vatAmount,
            totalAmount
          }
        });
      }
  
      // Previous Payments
      if (prevPayment > 0) {
        const taxableAmount = prevPayment;
        const vatAmount = Number((taxableAmount * 0.05).toFixed(2));
        const totalAmount = Number((taxableAmount * 1.05).toFixed(2));
  
        rows.push({
          sno: currentSno++,
          description: 'Less: Previous Payments',
          qty: 1,
          unitPrice: -taxableAmount,
          taxable: -taxableAmount,
          vatPercentage: 5,
          vatAmount: -vatAmount,
          totalAmount: -totalAmount,
          isDeduction: true,
          type: 'previous_payment',
          deductionData: {
            taxableAmount,
            vatAmount,
            totalAmount
          }
        });
      }
  
      // Performance Retention
      if (formData.deductions?.cumulativePerformanceRetention?.checked) {
        const percentage = formData.deductions.cumulativePerformanceRetention.percentage;
        const taxableAmount = Number((cumulativeValue * (percentage / 100)).toFixed(2));
        const vatAmount = Number((taxableAmount * 0.05).toFixed(2));
        const totalAmount = Number((taxableAmount * 1.05).toFixed(2));
  
        rows.push({
          sno: currentSno++,
          description: `Less: ${percentage}% Performance Retention of Total Amount`,
          qty: 1,
          unitPrice: -taxableAmount,
          taxable: -taxableAmount,
          vatPercentage: 5,
          vatAmount: -vatAmount,
          totalAmount: -totalAmount,
          isDeduction: true,
          type: 'performance_retention',
          deductionData: {
            percentage,
            taxableAmount,
            vatAmount,
            totalAmount
          }
        });
      }
  
      // Handle Other Deductions
      if (formData.deductions?.otherDeduction?.checked) {
        const showSubtotal = formData.deductions.otherDeduction.showSubtotal;
        
        if (showSubtotal) {
          // Add subtotal calculation
          const regularTotals = rows.reduce((acc, row) => ({
            taxable: acc.taxable + (Number(row.taxable) || 0),
            vatAmount: acc.vatAmount + (Number(row.vatAmount) || 0),
            totalAmount: acc.totalAmount + (Number(row.totalAmount) || 0)
          }), { taxable: 0, vatAmount: 0, totalAmount: 0 });
  
          rows.push({
            sno: currentSno++,
            description: 'Sub Total',
            qty: 1,
            unitPrice: regularTotals.taxable,
            taxable: regularTotals.taxable,
            vatPercentage: 5,
            vatAmount: regularTotals.vatAmount,
            totalAmount: regularTotals.totalAmount,
            isSubtotal: true,
            type: 'subtotal'
          });
        }
  
        // Add Other Deduction
        const amount = Number(parseFloat(formData.deductions.otherDeduction.amount).toFixed(2)) || 0;
        const includeVat = formData.deductions.otherDeduction.includeVat;
        
        if (includeVat) {
          const vatAmount = Number((amount * 0.05).toFixed(2));
          const totalAmount = Number((amount * 1.05).toFixed(2));
  
          rows.push({
            sno: currentSno++,
            description: 'Less: Other Deductions',
            qty: 1,
            unitPrice: -amount,
            taxable: -amount,
            vatPercentage: 5,
            vatAmount: -vatAmount,
            totalAmount: -totalAmount,
            isDeduction: true,
            type: 'other_deduction',
            deductionData: {
              taxableAmount: amount,
              vatAmount,
              totalAmount,
              includeVat
            }
          });
        } else {
          rows.push({
            sno: currentSno++,
            description: 'Less: Other Deductions',
            qty: 1,
            unitPrice: -amount,
            taxable: 'NA',
            vatPercentage: 'NA',
            vatAmount: 'NA',
            totalAmount: -amount,
            isDeduction: true,
            type: 'other_deduction',
            deductionData: {
              amount,
              includeVat
            }
          });
        }
      }
  
      return rows;
    } catch (error) {
      console.error('Error calculating rows:', error);
      return [];
    }
  };
  
  // Initialize customRowOrder when rows change
  useEffect(() => {
    const rows = calculateRows(formData);
    if (rows.length > 0 && customRowOrder.length === 0) {
      // Initialize with sequential indices (0, 1, 2, etc.)
      setCustomRowOrder(rows.map((_, index) => index));
    }
  }, [formData, customRowOrder.length]);

  // Function to get rows in custom order
  const getOrderedRows = () => {
    const originalRows = calculateRows(formData);
    
    // If no custom order or lengths don't match, return original rows
    if (customRowOrder.length === 0 || customRowOrder.length !== originalRows.length) {
      return originalRows;
    }
    
    // Return rows in the custom order
    return customRowOrder.map(index => {
      const row = originalRows[index];
      
      // Ensure we're not returning undefined rows
      if (!row) {
        console.error(`Row at index ${index} is undefined. Original rows length: ${originalRows.length}`);
        return originalRows[0]; // Fallback to first row
      }
      
      return row;
    });
  };

  // Function to move a row up or down
  const moveRow = (index, direction) => {
    if (customRowOrder.length === 0) {
      // If no custom order yet, initialize with sequential indices
      const rows = calculateRows(formData);
      const initialOrder = rows.map((_, idx) => idx);
      setCustomRowOrder(initialOrder);
      return;
    }
    
    // Make a copy of the current order
    const newOrder = [...customRowOrder];
    
    // Find the position of the index in the current order
    const currentPosition = newOrder.indexOf(index);
    
    // Check bounds
    if (direction === 'up' && currentPosition > 0) {
      // Swap with the item above
      [newOrder[currentPosition], newOrder[currentPosition - 1]] = 
      [newOrder[currentPosition - 1], newOrder[currentPosition]];
      setCustomRowOrder(newOrder);
    } else if (direction === 'down' && currentPosition < newOrder.length - 1) {
      // Swap with the item below
      [newOrder[currentPosition], newOrder[currentPosition + 1]] = 
      [newOrder[currentPosition + 1], newOrder[currentPosition]];
      setCustomRowOrder(newOrder);
    }
    
    // Force a re-render to update the display
    setForceUpdate(prev => !prev);
  };

  const calculateTotals = (rows) => {
    if (!rows?.length) return { taxable: 0, vatAmount: 0, totalAmount: 0 };
  
    // Check if we have other deduction row
    const otherDeductionRow = rows.find(row => row.type === 'other_deduction');
    const showSubtotal = formData.deductions?.otherDeduction?.showSubtotal;
    
    if (otherDeductionRow) {
      // Get all rows except the subtotal row
      const calculableRows = rows.filter(row => !row.isSubtotal);
      
      // Calculate totals including other deduction
      const totals = calculableRows.reduce((acc, row) => {
        // Handle NA values in other deduction row
        const rowTaxable = row.taxable === 'NA' ? 0 : Number(row.taxable || 0);
        const rowVatAmount = row.vatAmount === 'NA' ? 0 : Number(row.vatAmount || 0);
        const rowTotalAmount = Number(row.totalAmount || 0);
  
        return {
          taxable: acc.taxable + rowTaxable,
          vatAmount: acc.vatAmount + rowVatAmount,
          totalAmount: acc.totalAmount + rowTotalAmount
        };
      }, { taxable: 0, vatAmount: 0, totalAmount: 0 });
  
      return {
        taxable: Number(totals.taxable.toFixed(2)),
        vatAmount: Number(totals.vatAmount.toFixed(2)),
        totalAmount: Number(totals.totalAmount.toFixed(2))
      };
    }
  
    // Regular case without other deduction
    return rows.reduce((acc, row) => ({
      taxable: acc.taxable + (Number(row.taxable) || 0),
      vatAmount: acc.vatAmount + (Number(row.vatAmount) || 0),
      totalAmount: acc.totalAmount + (Number(row.totalAmount) || 0)
    }), { taxable: 0, vatAmount: 0, totalAmount: 0 });
  };

  // Handle Print and PDF Generation
  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;
    
    try {
      setIsGenerating(true);
      
      // Add specific styles for better table border rendering
      const styleSheet = document.createElement('style');
      styleSheet.textContent = `
        #invoice-content table {
          border-collapse: collapse !important;
          border-spacing: 0 !important;
        }
        #invoice-content td,
        #invoice-content th {
          border: 1px solid black !important;
          padding: 2px 4px !important;
          position: relative !important;
          background-clip: padding-box !important;
        }
        #invoice-content tr {
          break-inside: avoid !important;
        }
      `;
      document.head.appendChild(styleSheet);
      
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        windowWidth: invoiceRef.current.scrollWidth,
        windowHeight: invoiceRef.current.scrollHeight,
        logging: false,
        onclone: (clonedDoc) => {
          // Remove row control buttons from the cloned document
          const controls = clonedDoc.querySelectorAll('.row-controls');
          controls.forEach(control => control.remove());
          
          const styles = clonedDoc.createElement('style');
          styles.textContent = `
            @page { margin: 0; size: A4 portrait; }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            #invoice-content {
              width: 210mm !important;
              min-height: 297mm !important;
              padding: 25mm 10mm 25mm 8mm !important;
              margin: 0 !important;
            }
            table {
              border-collapse: collapse !important;
              border-spacing: 0 !important;
              width: 100% !important;
              margin-bottom: 1mm !important;
            }
            td, th {
              border: 1px solid black !important;
              padding: 2px 4px !important;
              position: relative !important;
              background-clip: padding-box !important;
            }
            tr {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .text-blue-500 { color: #3B82F6 !important; }
            .text-blue-600 { color: #2563EB !important; }
            .text-red-600 { color: #DC2626 !important; }
            .text-green-600 { color: #059669 !important; }
            .bg-yellow-300 { background-color: #FDE047 !important; }
            
            /* Hide row control buttons in PDF */
            .row-controls {
              display: none !important;
            }
          `;
          clonedDoc.head.appendChild(styles);
        }
      });
  
      // Remove the temporary style sheet
      document.head.removeChild(styleSheet);
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
        precision: 16
      });
  
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
      pdf.save(`Invoice-${formData.header?.serialNumber || 'download'}.pdf`);
  
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleSaveInvoice = async () => {
    if (!invoiceRef.current) return;
    
    try {setIsSaving(true);
        
      // Check if it's Ali & Sons client
      const isAliSons = client?.client_name?.includes("Ali & Sons");
      
      // Add specific styles for better table border rendering
      const styleSheet = document.createElement('style');
      styleSheet.textContent = `
          #invoice-content table {
              border-collapse: collapse !important;
              border-spacing: 0 !important;
          }
          #invoice-content td,
          #invoice-content th {
              border: 1px solid black !important;
              padding: 2px 4px !important;
              position: relative !important;
              background-clip: padding-box !important;
          }
          #invoice-content tr {
              break-inside: avoid !important;
          }
      `;
      document.head.appendChild(styleSheet);
      
      // Remove row control buttons before capture
      const controlButtons = invoiceRef.current.querySelectorAll('.row-controls');
      controlButtons.forEach(button => button.style.display = 'none');
      
      // Convert to canvas with improved settings
      const canvas = await html2canvas(invoiceRef.current, {
          scale: 3,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          windowWidth: invoiceRef.current.scrollWidth,
          windowHeight: invoiceRef.current.scrollHeight,
          logging: false
      });

      // Restore row control buttons
      controlButtons.forEach(button => button.style.display = '');

      // Remove temporary style sheet
      document.head.removeChild(styleSheet);

      // Create PDF with precise A4 dimensions
      const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true,
          precision: 16
      });

      // Convert to image and add to PDF
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');

      // Convert to blob
      const pdfBlob = await pdf.output('blob');
      const pdfFile = new File([pdfBlob], `${formData.header.serialNumber}.pdf`, {
          type: 'application/pdf'
      });

      // Get ordered rows for metadata
      const rows = getOrderedRows();
      const deductionValues = {};
      
      // Process rows and extract deduction values
      rows.forEach(row => {
          const type = getDeductionType(row.description);
          if (type) {
              deductionValues[type] = {
                  taxable: row.taxable === 'NA' ? 0 : Math.abs(parseFloat(row.taxable)) || 0,
                  vatAmount: row.vatAmount === 'NA' ? 0 : Math.abs(parseFloat(row.vatAmount)) || 0,
                  totalAmount: Math.abs(parseFloat(row.totalAmount)) || 0
              };
          }
      });

      // Create a formatted date object from certificate date
      const certDate = new Date(formData.details.certificateDate || formData.header.taxInvoiceDate);
      const monthYear = `${certDate.toLocaleString('default', { month: 'short' })} ${certDate.getFullYear()}`;

      // Store the row order in metadata
      const rowOrderMetadata = {
          custom_row_order: JSON.stringify(customRowOrder)
      };

      // Prepare metadata for save
      const metadata = {
        // Header Information
        serial_number: formData.header.serialNumber,
        tax_invoice_ref: formData.header.taxInvoiceRef,
        tax_invoice_date: formData.header.taxInvoiceDate,
        
        // Client & Project Info
        client_name: client.client_name,
        project_code: project.project_code,
        project_name: project.project_name,
        loa_wo_sub_contract_ref: project.loa_wo_sub_contract_ref,
        amount: finalAmount,
        trn: client.trn || '',
        address: client.address || '',
      
        // Certificate and Values
        payment_certificate_no: formData.details.paymentCertificateNo || '',
        certificate_date: formData.details.certificateDate,
        cumulative_value: parseFloat(formData.details.cumulativeValue || 0),
        previous_payment: parseFloat(formData.details.previousPayment || 0),
      
        // Custom Description
        custom_description: formData.details.customDescription || '',
        description_color: formData.details.descriptionColor || 'black',
        custom_amount: parseFloat(formData.details.customAmount || 0),
      
        // Advance Release - Updated with new fields
        advance_release_checked: formData.deductions.advanceRelease.checked,
        advance_release_percentage: formData.deductions.advanceRelease.checked ? 
            parseInt(formData.deductions.advanceRelease.percentage) : 0,
        advance_release_taxable: deductionValues.advance?.taxable || 0,
        advance_release_vat: deductionValues.advance?.vatAmount || 0,
        advance_release_total: deductionValues.advance?.totalAmount || 0,
        advance_release_use_fixed: formData.deductions.advanceRelease.useFixedAmount,
        advance_release_fixed_amount: parseFloat(formData.deductions.advanceRelease.fixedAmount || 0),
        advance_release_use_stored: formData.deductions.advanceRelease.useStoredAmount,
      
        // Retention Release
        retention_release_checked: formData.deductions.retentionRelease.checked,
        retention_release_percentage: formData.deductions.retentionRelease.checked ? 
            parseInt(formData.deductions.retentionRelease.percentage) : 0,
        retention_release_taxable: deductionValues.retention?.taxable || 0,
        retention_release_vat: deductionValues.retention?.vatAmount || 0,
        retention_release_total: deductionValues.retention?.totalAmount || 0,
      
        // Cumulative Retention
        cumulative_retention_checked: formData.deductions.cumulativeretention.checked,
        cumulative_retention_percentage: formData.deductions.cumulativeretention.checked ? 
            parseInt(formData.deductions.cumulativeretention.percentage) : 0,
        cumulative_retention_taxable: deductionValues.cumulative?.taxable || 0,
        cumulative_retention_vat: deductionValues.cumulative?.vatAmount || 0,
        cumulative_retention_total: deductionValues.cumulative?.totalAmount || 0,
      
        // Performance Retention
        cumulative_performance_retention_checked: formData.deductions.cumulativePerformanceRetention.checked,
        cumulative_performance_retention_percentage: formData.deductions.cumulativePerformanceRetention.checked ? 
            parseInt(formData.deductions.cumulativePerformanceRetention.percentage) : 0,
        cumulative_performance_retention_taxable: deductionValues.performance?.taxable || 0,
        cumulative_performance_retention_vat: deductionValues.performance?.vatAmount || 0,
        cumulative_performance_retention_total: deductionValues.performance?.totalAmount || 0,
      
        // NCR Deduction
        ncr_deduction_checked: formData.deductions.ncrDeduction.checked,
        ncr_deduction_amount: deductionValues.ncr?.taxable || 0,
        ncr_deduction_vat: deductionValues.ncr?.vatAmount || 0,
        ncr_deduction_total: deductionValues.ncr?.totalAmount || 0,
      
        // Previous Payment amounts
        previous_payment_taxable: deductionValues.previous?.taxable || 0,
        previous_payment_vat: deductionValues.previous?.vatAmount || 0,
        previous_payment_total: deductionValues.previous?.totalAmount || 0,
      
        // Other Deduction
        other_deduction_checked: formData.deductions.otherDeduction.checked,
        other_deduction_amount: deductionValues.other?.taxable || 0,
        other_deduction_vat: deductionValues.other?.vatAmount || 0,
        other_deduction_total: deductionValues.other?.totalAmount || 0,
        other_deduction_include_vat: formData.deductions.otherDeduction.checked ? 
            formData.deductions.otherDeduction.includeVat : false,
        other_deduction_show_subtotal: formData.deductions.otherDeduction.checked ? 
            formData.deductions.otherDeduction.showSubtotal : false,
      
        // Row order
        custom_row_order: JSON.stringify(customRowOrder),
        
        // Bank Details
        bank_name_branch: formData.bankDetails.bank === 'ADCB' 
            ? 'ABU DHABI COMMERCIAL BANK PJSC, Dubai, UAE' 
            : 'INVEST BANK PJSC, Dubai, UAE',
        account_number: formData.bankDetails.accountNumber,
        iban: formData.bankDetails.iban,
        swift: formData.bankDetails.swift,
      
        total_wd_last_period: parseFloat(formData.details.totalWDLastPeriod || 0),
      
        // Add Ali & Sons specific fields if applicable
        ...(isAliSons ? {
            landscape_preliminaries: parseFloat(formData.details.billItems?.landscape?.preliminaries?.amount || 0),
            landscape_works: parseFloat(formData.details.billItems?.landscape?.landscapingWorks?.amount || 0),
            tenderers_adjustments: parseFloat(formData.details.billItems?.landscape?.tenderersAdjustments?.amount || 0),
            provisional_sums: parseFloat(formData.details.billItems?.landscape?.ProvisionalSums?.amount || 0),
            swimming_preliminaries: parseFloat(formData.details.billItems?.swimming?.preliminaries?.amount || 0),
            swimming_mep: parseFloat(formData.details.billItems?.swimming?.mepWorks?.amount || 0),
            swimming_civil: parseFloat(formData.details.billItems?.swimming?.civilWorks?.amount || 0)
        } : {}),

        // Add formatted filename for taxinvoiceslist folder
        formatted_filename: generateFormattedFilename(
          formData.header.serialNumber,
          formData.header.taxInvoiceRef,
          client.client_name,
          project.project_name,
          formData.details.paymentCertificateNo,
          monthYear,
          project.project_code
        )
      };

      // Function to generate formatted filename
      function generateFormattedFilename(serialNumber, taxInvoiceRef, clientName, projectName, paymentCertNo, monthYear, projectCode) {
        // Clean up values and ensure they're safe for filenames
        const cleanStr = (str) => str ? str.replace(/[\/\\:*?"<>|]/g, '-').trim() : '';
        
        const cleanSerialNumber = cleanStr(serialNumber);
        const cleanTaxInvoiceRef = cleanStr(taxInvoiceRef);
        const cleanClientName = cleanStr(clientName).split(',')[0]; // Take just the first part if there are commas
        const cleanProjectName = cleanStr(projectName).split('@')[0].trim(); // Remove anything after @
        const cleanPaymentCertNo = cleanStr(paymentCertNo);
        const cleanProjectCode = cleanStr(projectCode);
        
        // Format: "Tax Invoice # 001, Inv # 08, Island Oasis, Gate House, PC#08, Nov 2024, PJ2407.pdf"
        return `Tax Invoice # ${cleanSerialNumber}, Inv # ${cleanTaxInvoiceRef}, ${cleanClientName}, ${cleanProjectName}, PC# ${cleanPaymentCertNo}, ${monthYear}, ${cleanProjectCode}.pdf`;
      }

      // Save invoice with the added taxinvoiceslist option and row order
      const result = await invoiceService.saveInvoiceWithTaxInvoicesList(pdfFile, metadata);

      if (result.status === 'success') {
          toast({
              title: "Success",
              description: result.is_update ? 
                  "Invoice updated successfully" : 
                  "Invoice created successfully",
              variant: "success"
          });
          onBack();
      } else {
          throw new Error(result.message || 'Failed to save invoice');
      }

  } catch (error) {
      console.error('Save invoice error:', error);
      toast({
          title: "Error",
          description: error.message || "Failed to save invoice",
          variant: "destructive"
      });
  } finally {
      setIsSaving(false);
  }
};

const getDeductionType = (description) => {
if (!description) return null;
if (description.includes('Advance')) return 'advance';
if (description.includes('Retention Release')) return 'retention';
if (description.includes('Cumulative Retention') && !description.includes('Performance')) return 'cumulative';
if (description.includes('Performance')) return 'performance';
if (description.includes('NCR')) return 'ncr';
if (description.includes('Previous Payments')) return 'previous';
if (description.includes('Other Deductions')) return 'other';
return null;
};

const handlePrint = () => {
window.print();
};

const getFinalAmount = () => {
const totalKey = 'total-totalAmount';
if (manualAmounts[totalKey] !== undefined) {
  return Math.abs(manualAmounts[totalKey]);
}
return Math.abs(calculateFinalAmount(getOrderedRows()));
};

const calculateFinalAmount = (rows) => {
if (!rows?.length) return 0;

// Helper function to get adjusted amount if it exists
const getAdjustedAmount = (row, index) => {
  const manualKey = `${index}-totalAmount`;
  return manualAmounts[manualKey] !== undefined ? 
    manualAmounts[manualKey] : 
    Number(row.totalAmount) || 0;
};

if (rows.some(row => row.type === 'other_deduction')) {
  const regularRows = rows.filter(row => row.type !== 'other_deduction' && !row.isSubtotal);
  
  // Calculate subtotal with possible manual adjustments
  const subtotalAmount = regularRows.reduce((acc, row, index) => 
    acc + getAdjustedAmount(row, index), 0);

  // Get other deduction row and its possible manual adjustment
  const otherDeductionRow = rows.find(row => row.type === 'other_deduction');
  const otherDeductionIndex = rows.findIndex(row => row.type === 'other_deduction');
  const deductionAmount = Math.abs(getAdjustedAmount(otherDeductionRow, otherDeductionIndex));

  // Check for manual adjustment to the final total
  const finalTotal = Number((subtotalAmount - deductionAmount).toFixed(2));
  const manualTotalKey = 'total-totalAmount';
  
  return manualAmounts[manualTotalKey] !== undefined ? 
    manualAmounts[manualTotalKey] : 
    finalTotal;
}

// Regular case without other deduction
const regularTotal = rows.reduce((acc, row, index) => {
  if (row.isSubtotal || row.type === 'other_deduction') return acc;
  return acc + getAdjustedAmount(row, index);
}, 0);

// Check for manual adjustment to the final total
const manualTotalKey = 'total-totalAmount';
return manualAmounts[manualTotalKey] !== undefined ? 
  manualAmounts[manualTotalKey] : 
  Number(regularTotal.toFixed(2));
};

const rows = calculateRows(formData);
const orderedRows = getOrderedRows();
const totals = calculateTotals(orderedRows);
const finalAmount = calculateFinalAmount(orderedRows);

return (
<div className="min-h-screen bg-gray-50">
  <div 
    id="invoice-content"
    ref={invoiceRef}
    className="w-[210mm] mx-auto bg-white shadow-lg"
    style={{
      minHeight: '297mm',
      padding: '25mm 10mm 25mm 8mm'
    }}
  >
    {/* Header Section */}
    <div className="mt-0">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-blue-500 text-xl font-bold underline">TAX INVOICE</div>
          <div className="text-red-600 font-bold border-b border-red-600 inline-block">
            S.N.: {formData.header?.serialNumber}...{new Date().getFullYear()}
          </div>
          <div className="font-bold leading-tight text-[11px]">Tax Invoice Ref: {formData.header?.taxInvoiceRef}</div>
          <div className="font-bold leading-tight text-[11px]">Tax Invoice Date: {formatDate(formData.header?.taxInvoiceDate)}</div>
        </div>
      </div>
    </div>

    {/* Address Section */}
    <div className="relative mt-4">
      {/* From Section */}
      <div className="mb-2">
        <div className="flex items-start text-[11px] leading-tight">
          <span className="font-bold underline">From: </span>
          <span className="ml-1 font-bold">Phoenician Technical Services LLC.</span>
        </div>
        <div className="text-[11px] leading-tight font-bold">P.O Box: 377972, Dubai, UAE</div>
        <div className="text-[11px] leading-tight font-bold">Central Plaza, Office 399, DIP-1</div>
        <div className="text-[11px] leading-tight font-bold">Tel: 04-8829484 Fax: 04-8829585</div>
        <div className="relative inline-block">
          <span className="text-green-600 text-[11px] font-bold">TRN: 100058522200003</span>
          <div className="absolute bottom-0 left-0 w-full h-0.5 bg-green-600"></div>
        </div>
      </div>

      {/* To Section */}
      <div className="mb-2">
        <div className="flex items-start text-[11px] leading-tight">
          <span className="font-bold underline">To: </span>
          <span className="ml-1 font-bold">{client?.client_name}</span>
        </div>
        <div className="text-[11px] leading-tight font-bold">{client?.address}</div>
        <div className="text-[11px] leading-tight font-bold">Tel: {client?.telephone_no} FAX: {client?.fax_no}</div>
        <div className="relative inline-block">
          <span className="text-green-600 text-[11px] font-bold">TRN: {client?.trn}</span>
          <div className="absolute bottom-0 left-0 w-full h-0.5 bg-green-600"></div>
        </div>
      </div>

      {/* Project Details */}
      <div className="absolute right-0 top-0 text-right text-blue-600 font-bold text-[11px] leading-tight">
        <div>{`Project: ${project?.project_name?.split('@')[0]}`}</div>
        <div>
          <span className="ml-28">{project?.project_name?.split('@')[1]}</span>
        </div>
        <div>{project?.loa_wo_sub_contract_ref?.split('dated')[0]}</div>
        <div>
          <span className="ml-28">{`dated ${project?.loa_wo_sub_contract_ref?.split('dated')[1]}`}</span>
        </div>
      </div>
    </div>

    {/* Contract Value */}
    <div className="text-blue-600 font-bold mb-4 text-[11px]">
      Contract Value: AED {formatNumber(project?.contract_value)}
    </div>

    {/* Table Section */}
    <div className="mb-4">
      <table className="w-full border-collapse border border-black [border-style:solid] [border-width:1px]">
        <thead>
          <tr className="h-7">
            <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 w-6 text-center text-[11px] font-bold">
              S.NO
            </th>
            <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-center w-[30%] text-[11px] font-bold">
              Description
            </th>
            <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 w-20 text-center text-[11px] font-bold">
              Qty
            </th>
            <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 w-40 text-center text-[11px] font-bold">
              Unit Price
            </th>
            <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 w-40 text-center text-[11px] font-bold">
              Taxable
            </th>
            <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 w-14 text-center text-[11px] font-bold">
              VAT %
            </th>
            <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 w-40 text-center text-[11px] font-bold">
              VAT Amount
            </th>
            <th className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 w-44 text-center text-[11px] font-bold">
              Total Amount<br />INCL VAT<br />(in AED)
            </th>
            <th className="border-none bg-transparent print-hidden w-12"></th>
          </tr>
        </thead>
        <tbody>
          {orderedRows.map((row, displayIndex) => {
            // Find the original index of this row in the calculated rows
            const originalIndex = rows.findIndex(r => r.type === row.type);
            
            return (
              <tr key={row.sno} className={`${row.isDeduction ? 'text-red-600' : ''} h-[22px]`}>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 text-center text-[11px] font-bold">
                  {displayIndex + 1}
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-[11px] font-bold"
                  dangerouslySetInnerHTML={{ __html: row.description }}>
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 text-center text-[11px] font-bold">
                  {row.qty}
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right text-[11px] font-bold">
                  <DecimalEditor value={row.unitPrice} identifier={displayIndex} field="unitPrice" />
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right text-[11px] font-bold">
                  <DecimalEditor value={row.taxable} identifier={displayIndex} field="taxable" />
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-0.5 text-center text-[11px] font-bold">
                  {row.vatPercentage === 'NA' ? (
                    <div className="text-center">NA</div>
                  ) : (
                    <div className="text-center">{row.vatPercentage}%</div>
                  )}
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right text-[11px] font-bold">
                  <DecimalEditor value={row.vatAmount} identifier={displayIndex} field="vatAmount" />
                </td>
                <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right text-[11px] font-bold">
                  <DecimalEditor value={row.totalAmount} identifier={displayIndex} field="totalAmount" />
                </td>
                <td className="border-none bg-transparent print-hidden">
                  <div className="row-controls flex items-center justify-center space-x-1">
                    <button 
                      onClick={() => moveRow(originalIndex, 'up')}
                      disabled={displayIndex === 0}
                      className={`p-1 rounded ${displayIndex === 0 ? 'text-gray-400' : 'text-blue-600 hover:bg-blue-100'}`}
                      title="Move Up"
                    >
                      <MoveUp size={16} />
                    </button>
                    <button 
                      onClick={() => moveRow(originalIndex, 'down')}
                      disabled={displayIndex === orderedRows.length - 1}
                      className={`p-1 rounded ${displayIndex === orderedRows.length - 1 ? 'text-gray-400' : 'text-blue-600 hover:bg-blue-100'}`}
                      title="Move Down"
                    >
                      <MoveDown size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}

          {/* Total Row */}
          <tr className="h-[22px]">
            <td colSpan="4" className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-center font-bold text-[11px]">
              Total Amount Due for Payment Including VAT (in AED)
            </td>
            <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right font-bold text-[11px]">
              <DecimalEditor value={totals.taxable} identifier="total" field="taxable" isTotal={true} />
            </td>
            <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-center font-bold text-[11px]">
              5%
            </td>
            <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right font-bold text-[11px]">
              <DecimalEditor value={totals.vatAmount} identifier="total" field="vatAmount" isTotal={true} />
            </td>
            <td className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 text-right font-bold text-[11px]">
              <DecimalEditor value={totals.totalAmount} identifier="total" field="totalAmount" isTotal={true} />
            </td>
            <td className="border-none bg-transparent print-hidden"></td>
          </tr>

          {/* Amount in Words Row */}
          <tr className="h-[22px]">
            <td colSpan="8" className="border border-black [border-style:solid] [border-width:1px] py-0.5 px-1 font-bold text-center text-[11px]">
              AED: {numberToWords(getFinalAmount())}
            </td>
            <td className="border-none bg-transparent print-hidden"></td>
          </tr>
        </tbody>
      </table>
    </div>

    {/* Bank Details and Remarks */}
    <div className="mt-4" style={{ marginBottom: '25mm' }}>
      {/* Bank Details */}
      <div>
        <div className="font-bold underline mb-2 text-[11px]">Bank Details:</div>
        <div className="space-y-1">
          <div className="text-[11px] leading-tight font-bold">
            Account Name: {formData.bankDetails?.accountName}
          </div>
          <div className="text-[11px] leading-tight flex items-start">
            <span className="font-bold whitespace-nowrap">Bank Name & Branch:</span>
            <span className="bg-yellow-300 px-1 font-bold ml-1">
              {formData.bankDetails?.bank === 'ADCB' 
                ? 'ABU DHABI COMMERCIAL BANK PJSC, Dubai, U.A.E.' 
                : 'INVEST BANK PJSC, Dubai, U.A.E.'}
            </span>
          </div>
          <div className="text-[11px] leading-tight font-bold">
            Account Number: {formData.bankDetails?.accountNumber}
          </div>
          <div className="text-[11px] leading-tight font-bold">
            IBAN: {formData.bankDetails?.iban}
          </div>
          <div className="text-[11px] leading-tight font-bold">
            SWIFT: {formData.bankDetails?.swift}
          </div>
        </div>
      </div>

      {/* Remarks Section */}
      <div className="mt-4">
        <div className="font-bold mb-1 text-[11px]">Remarks:</div>
        <div className="flex items-start">
          <span className="mr-1 font-bold">•</span>
          <div className="font-bold text-[11px] leading-tight flex flex-wrap">
            <span>If any questions concerning this Invoice, please contact Eng. Elie Harb, the General Manager, on </span>
            <span className="text-blue-600 mx-1">+971557877701</span>
            <span>, Email: </span>
            <span className="text-blue-600 inline-block">elie@phoenician-uae.com</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* Control Buttons */}
  <div className="fixed bottom-4 right-4 space-x-2 print:hidden">
    <button
      onClick={onBack}
      className="px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200 text-sm"
    >
      Edit
    </button>

    <button
      onClick={handleSaveInvoice}
      disabled={isSaving}
      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-2"
    >
      <Save className="w-4 h-4" />
      {isSaving ? 'Saving...' : 'Save Invoice'}
    </button>

    <button
      onClick={handleDownloadPDF}
      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center gap-2"
    >
      <Download className="w-4 h-4" />
      Download PDF
    </button>

    <button
      onClick={handlePrint}
      className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm flex items-center gap-2"
    >
      <Printer className="w-4 h-4" />
      Print Invoice
    </button>
  </div>      

  {/* Loading Overlay */}
  {isGenerating && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:hidden">
      <div className="bg-white p-4 rounded-lg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-sm">Generating PDF... Please wait</p>
      </div>
    </div>
  )}
</div>
);
};

export default InvoicePreview;
